// ============================================================================
// useInvestmentData — Combina Bling snapshot (saldo real CDB) + Firestore
// (aportes manuais) para fornecer dados consolidados ao InvestmentDashboard.
//
// Fontes:
//   1. Bling snapshot → kpis.saldo_reserva_cdb (saldo real do portador CDB PJ)
//   2. Firestore investments collection → aportes individuais registrados
//
// O hook entrega:
//   - currentTotal: saldo real do CDB (Bling) OU soma dos aportes (fallback)
//   - blingConnected: true se snapshot tem dados de portadores
//   - saldoReservaCDB: valor direto do Bling
//   - saldoTotalPortadores: soma de todos os portadores ativos
//   - portadores: lista completa [{id, nome, saldo, situacao}]
// ============================================================================

import { useMemo } from 'react';
import { useRectaBlingSnapshot, getSaldoReservaCDB, getSaldoTotalPortadores, getPortadores } from './useRectaBlingSnapshot';

/**
 * @param {Array} firestoreItems — items do useInvestments() (collection Firestore)
 * @returns {object} dados consolidados para o dashboard
 */
export function useInvestmentData(firestoreItems = []) {
  const { data: snapshot, loading: blingLoading } = useRectaBlingSnapshot();

  const result = useMemo(() => {
    const saldoReservaCDB = getSaldoReservaCDB(snapshot);
    const saldoTotalPortadores = getSaldoTotalPortadores(snapshot);
    const portadores = getPortadores(snapshot);
    const blingConnected = portadores.length > 0;

    // Soma dos aportes manuais no Firestore (fallback se Bling não tem dados)
    const firestoreTotal = firestoreItems
      .filter(i => i.status === 'ACTIVE')
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

    // Prioridade: saldo real do Bling > soma manual do Firestore
    const currentTotal = blingConnected && saldoReservaCDB > 0
      ? saldoReservaCDB
      : firestoreTotal;

    return {
      currentTotal,
      firestoreTotal,
      saldoReservaCDB,
      saldoTotalPortadores,
      portadores,
      blingConnected,
      blingLoading,
    };
  }, [snapshot, firestoreItems, blingLoading]);

  return result;
}
